import { CZECHIA_BOUNDS, SLOVENIA_BOUNDS } from '../../config/constants.js';
import { translate, getCurrentLanguage } from '../../utils/i18n.js';

/**
 * CDN base URL for map styles
 */
const STYLE_CDN_BASE = 'https://tiles.kingprostor.si/assets/map-styles';

/**
 * Local build of the same artifacts (`pnpm build:style:local` writes
 * public/styles/local/, which the Vite dev server serves at this path).
 */
const LOCAL_STYLE_BASE = '/styles/local';

/**
 * Server runtime env ('dev' | 'beta' | 'prod'), from GET /api/v1/config/bootstrap.
 * Must be set BEFORE the map is constructed (the first style fetch resolves the
 * variant) — aimaps awaits initServerEnv() pre-MapManager, in parallel with the
 * prefs load. Null (bootstrap failed / not called) = prod-style default.
 */
let serverEnv: string | null = null;

/**
 * Fetch + remember the server runtime env for style-variant resolution.
 * Cheap: browser-cached 1h, runs in parallel with other pre-map awaits.
 * Failure silently leaves the prod-style default.
 */
export async function initServerEnv(): Promise<void> {
  if (serverEnv !== null) return;
  try {
    const res = await fetch('/api/v1/config/bootstrap');
    if (res.ok) {
      const body = (await res.json()) as { env?: string };
      if (typeof body.env === 'string') serverEnv = body.env;
    }
  } catch {
    // default: prod styles
  }
}

/** Test/edge hook (e.g. Playwright forcing a variant without the endpoint). */
export function setServerEnv(env: string | null): void {
  serverEnv = env;
}

type StyleVariant = 'local' | 'dev' | 'prod';

/**
 * Which style build to load. An explicit ?mapstyle= override wins
 * (local|dev|prod — `local` previews a style edit on the local app without a
 * CDN upload, dev|prod cross-test the CDN variants); otherwise the dev env
 * defaults to its own styles — dev styles reference dev's tile vintages,
 * matching the dev DB popups.
 */
function styleVariant(): StyleVariant {
  if (typeof window === 'undefined') return 'prod';
  const params = new URLSearchParams(window.location.search);
  const override = params.get('mapstyle');
  if (override === 'local' || override === 'dev' || override === 'prod') return override;
  return serverEnv === 'dev' ? 'dev' : 'prod';
}

/**
 * Style JSON URL for a CDN: basemap — the local build, or the CDN artifact
 * with the -dev suffix when the dev variant applies.
 */
function getCdnStyleUrl(styleName: string): string {
  const variant = styleVariant();
  if (variant === 'local') return `${LOCAL_STYLE_BASE}/${styleName}.json`;
  const suffix = variant === 'dev' ? '-dev' : '';
  return `${STYLE_CDN_BASE}/${styleName}${suffix}.json`;
}

export interface BasemapSource {
  id: string;
  type: 'raster' | 'vector' | 'raster-dem';
  url?: string; // For PMTiles or style URL
  tiles?: string[]; // For raster tiles
  attribution?: string;
  minzoom?: number;
  maxzoom?: number;
  tileSize?: number;
  bounds?: [number, number, number, number];
  encoding?: 'terrarium' | 'mapbox'; // For raster-dem tiles
}

export interface BasemapLayer {
  id: string;
  type: 'raster' | 'fill' | 'line' | 'symbol' | 'circle';
  source: string;
  'source-layer'?: string;
  paint?: any;
  layout?: any;
  minzoom?: number;
  maxzoom?: number;
}

/** Credit line a basemap requires on the map while it is shown. */
export interface BasemapCredit {
  label: string;
  href: string;
}

export interface BasemapConfig {
  id: string;
  name: string;
  /** i18n key that wins over `name` when set (names shown in the picker). */
  nameKey?: string;
  type: 'vector' | 'raster';
  styleUrl?: string; // URL to style JSON for vector basemaps
  previewUrl?: string;
  /**
   * WGS84 [west, south, east, north] coverage of a region-specific basemap.
   * A map whose config lists no `basemaps` offers only the basemaps that reach
   * it (countrySettings.allowedBasemapIds). Absent = worldwide.
   */
  bounds?: [number, number, number, number];
  /** Credit the licence asks for while this basemap is shown (AttributionBar). */
  attribution?: BasemapCredit;
  source?: BasemapSource; // Only needed for raster sources
  layer?: BasemapLayer; // Only needed for raster sources
}

/**
 * Basemap configurations for GSX8
 */
export const basemaps = {
  'pm-light': {
    id: 'pm-light',
    name: 'PM Light',
    type: 'vector',
    styleUrl: 'CDN:pm-light-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/pm-light.png`,
  },

  'pm-dark': {
    id: 'pm-dark',
    name: 'PM Dark',
    type: 'vector',
    styleUrl: 'CDN:pm-dark-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/pm-dark.png`,
  },

  'osm-light': {
    id: 'osm-light',
    name: 'Light',
    type: 'vector',
    styleUrl: 'CDN:osm-light-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-light.png`,
  },

  'osm-omt': {
    id: 'osm-omt',
    name: 'OMT',
    type: 'vector',
    styleUrl: 'CDN:osm-omt-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-omt.png`,
  },

  'osm-basic': {
    id: 'osm-basic',
    name: 'Basic',
    type: 'vector',
    styleUrl: 'CDN:osm-basic-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-basic.png`,
  },

  'osm-dark': {
    id: 'osm-dark',
    name: 'Dark',
    type: 'vector',
    styleUrl: 'CDN:osm-dark-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-dark.png`,
  },

  'osm-winter': {
    id: 'osm-winter',
    name: 'Winter',
    type: 'vector',
    styleUrl: 'CDN:osm-winter-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-winter.png`,
  },

  'osm-dataviz': {
    id: 'osm-dataviz',
    name: 'Dataviz',
    type: 'vector',
    styleUrl: 'CDN:osm-dataviz-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/osm-dataviz.png`,
  },

  raster: {
    id: 'raster',
    name: 'Raster',
    nameKey: 'basemapRaster',
    type: 'raster',
    styleUrl: 'CDN:raster-gurs',
    previewUrl: `https://tiles.kingprostor.si/assets/previews/raster.png`,
    // The GURS DOF tiles; the style JSON carries the same box on its source.
    bounds: SLOVENIA_BOUNDS,
  },

  // ČÚZK Ortofoto ČR straight from the office's Web Mercator service, as 512 px
  // export renders (#594 option 1: no proxy, no cache on our side; 12 requests
  // per full-HD view where its 256 px cache tiles need 35; the trade-offs are in
  // docs/czk/specs.md). The style JSON is served from our own origin
  // (public/styles/, like the raster source style) and carries the same bounds
  // and attribution on its source; tests/unit/client/map/base/basemaps.test.ts
  // keeps the two in step. No hillshade or terrain: raster basemaps never get
  // them, and the DEM is Slovenian anyway.
  'cuzk-ortofoto': {
    id: 'cuzk-ortofoto',
    name: 'ČÚZK Ortofoto',
    nameKey: 'basemapCuzkOrtofoto',
    type: 'raster',
    styleUrl: '/styles/cuzk-ortofoto.json',
    previewUrl: '/previews/cuzk-ortofoto.png',
    bounds: CZECHIA_BOUNDS,
    attribution: {
      // "ČÚZK – on-line" is the provider format the network-services terms prescribe.
      label: 'ČÚZK – on-line — Ortofoto ČR (CC BY 4.0)',
      href: 'https://geoportal.cuzk.gov.cz/',
    },
  },
} as const satisfies Record<string, BasemapConfig>;

/**
 * Get basemap configuration by ID
 */
export function getBasemap(id: string): BasemapConfig | undefined {
  return basemaps[id as keyof typeof basemaps];
}

/**
 * Which office's reference data the style carries. Mirrors `referenceData` on
 * the map config (#591): a ČÚZK-backed map loads the czk family, everything
 * else the gurs family it has always loaded.
 */
export type StyleFamily = 'gurs' | 'czk';

/**
 * Basemaps that have a czk pair on the CDN. Must match CZK_BASEMAPS in
 * scripts/build-style.ts: the eight vector basemaps, not the Slovenian DOF
 * raster and not the ČÚZK ortofoto (its style lives on our own origin and
 * points at the office's tile host, which the style content gate does not allow on
 * the CDN). A basemap outside this set keeps its gurs style, which on a Czech
 * map simply means no baked reference layers.
 */
const CZK_STYLE_BASEMAPS: ReadonlySet<string> = new Set([
  'pm-light',
  'pm-dark',
  'osm-light',
  'osm-omt',
  'osm-basic',
  'osm-dark',
  'osm-winter',
  'osm-dataviz',
]);

/** Does this basemap have a czk variant on the CDN? */
export function hasCzkStyleVariant(basemapId: string): boolean {
  return CZK_STYLE_BASEMAPS.has(basemapId);
}

/**
 * Resolve style URL from basemap config.
 * Handles CDN: prefix for styles served from tiles.kingprostor.si CDN.
 * Supports ?mapstyle=local|dev|prod for testing a build (see styleVariant).
 *
 * @param basemap - The basemap configuration
 * @param options.useGursOffVariant - If true, uses the -off variant where the
 *   reference layers are hidden by default (tenant maps turn back on what their
 *   config asks for). Named for GURS for history; it governs both families.
 * @param options.family - Which reference-data family to load. Defaults to
 *   'gurs', so every existing caller resolves exactly the URL it did before.
 */
export function getResolvedStyleUrl(
  basemap: BasemapConfig,
  options?: { useGursOffVariant?: boolean; family?: StyleFamily }
): string | undefined {
  if (!basemap.styleUrl) return undefined;

  // Handle CDN: prefix for styles served from CDN
  if (basemap.styleUrl.startsWith('CDN:')) {
    let styleName = basemap.styleUrl.slice(4); // Remove 'CDN:' prefix

    if (options?.family === 'czk' && hasCzkStyleVariant(basemap.id)) {
      styleName = styleName.replace('-gurs', options.useGursOffVariant ? '-czk-off' : '-czk');
    } else if (options?.useGursOffVariant) {
      // Use -off variant for tenant maps (GURS hidden by default)
      styleName = styleName.replace('-gurs', '-gurs-off');
    }

    return getCdnStyleUrl(styleName);
  }

  // Regular local styles
  return basemap.styleUrl;
}

/**
 * Get basemap name with i18n support
 */
export function getBasemapName(id: string): string {
  const basemap: BasemapConfig | undefined = basemaps[id as keyof typeof basemaps];
  if (!basemap) return id;

  if (basemap.nameKey) {
    return translate(basemap.nameKey, getCurrentLanguage());
  }

  return basemap.name;
}

/**
 * Get all available basemap configurations with translated names
 */
export function getAllBasemaps(): BasemapConfig[] {
  return Object.values(basemaps).map(basemap => ({
    ...basemap,
    name: getBasemapName(basemap.id)
  }));
}

/**
 * Get all available basemap IDs
 */
export function getBasemapIds(): string[] {
  return Object.keys(basemaps);
}

/**
 * The WGS84 coverage of a region-specific basemap, undefined for a worldwide
 * one (and for an unknown id).
 */
export function basemapBounds(id: string): [number, number, number, number] | undefined {
  return getBasemap(id)?.bounds;
}

/**
 * Check if a basemap requires bounds (is region-specific)
 */
export function basemapHasBounds(id: string): boolean {
  return basemapBounds(id) !== undefined;
}

/** Aerial imagery rather than a drawn map: no hillshade, no terrain on it. */
export function isRasterBasemap(id: string): boolean {
  return getBasemap(id)?.type === 'raster';
}

/** The credit line the basemap asks for, undefined when it needs none. */
export function basemapCredit(id: string): BasemapCredit | undefined {
  return getBasemap(id)?.attribution;
}

/**
 * Where the basemap icon click goes (#591, #594): the vector/aerial flip when
 * the allowed list holds an aerial (raster type) basemap, otherwise the next
 * allowed style after the current one. Null when there is nothing to flip to.
 * `allowed` is the map's allowed list in registry order.
 */
export function basemapFlipTarget(allowed: BasemapConfig[], currentId: string): string | null {
  if (allowed.length < 2) return null;
  const ids = allowed.map((b) => b.id);
  const aerial = allowed.find((b) => b.type === 'raster');
  if (aerial) {
    if (!isRasterBasemap(currentId)) return aerial.id;
    const home = getDefaultBasemap().id;
    return ids.includes(home) ? home : (ids.find((id) => !isRasterBasemap(id)) ?? null);
  }
  const i = ids.indexOf(currentId);
  return ids[(i + 1) % ids.length] ?? null;
}

/**
 * Get default basemap (light)
 */
export function getDefaultBasemap(): BasemapConfig {
  return basemaps['osm-basic'];
}

/**
 * Terrain source for 3D elevation (View 2.5D)
 */
export const terrainSource: BasemapSource = {
  id: 'terrain-rgb',
  type: 'raster-dem',
  tiles: ['https://tiles.kingprostor.si/data/si/trgb/trgb-512/tiles-20251017/{z}/{x}/{y}.png'],
  tileSize: 512,
  encoding: 'mapbox',
  minzoom: 10,
  maxzoom: 14,
  bounds: SLOVENIA_BOUNDS,
};

/**
 * Hillshade source for 2D terrain shading
 * Uses same tiles as terrain but separate source for better render quality
 */
export const hillshadeSource: BasemapSource = {
  id: 'hillshade-rgb',
  type: 'raster-dem',
  tiles: ['https://tiles.kingprostor.si/data/si/trgb/trgb-512/tiles-20251017/{z}/{x}/{y}.png'],
  tileSize: 512,
  encoding: 'mapbox',
  minzoom: 10,
  maxzoom: 14,
  bounds: SLOVENIA_BOUNDS,
};
